import styles from './configurator.module.scss'

import { cyan, generate, green, presetPalettes, red } from '@ant-design/colors'
import { Col, ColorPicker, Divider, Row, theme } from 'antd'
import type { ColorPickerProps } from 'antd'
import { useScreenWidth } from 'app/hooks/useScreenWidth'
import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { UtilService } from 'common/services/utilService'
import { useEffect, useState } from 'react'
const { capitalizeFirstLetter } = UtilService

// Update the type definition to include the key property
type Presets = Required<ColorPickerProps>['presets'][number] & { key?: string }

function genPresets(presets = presetPalettes, isMobile = false) {
  return Object.entries(presets).map<Presets>(([label, colors]) => ({
    label: capitalizeFirstLetter(label),
    colors,
    defaultOpen: !isMobile,
  }))
}

// Get CSS variables from :root
function getCSSVariables(category: string) {
  const styles = getComputedStyle(document.documentElement)
  const colors: string[] = []
  const categories = ['primary', 'secondary', 'tertiary', 'quaternary']
  if (categories.includes(category)) {
    const values = [100, 80, 60, 40, 20]
    values.forEach((val) => {
      const varName = `--${category}-${val}`
      const color = styles.getPropertyValue(varName).trim()
      if (color) colors.push(color)
    })
  }
  return colors
}

type Props = {
  onChange: (color: string) => void
  value: string
  defaultColor: string
  children: React.ReactNode
}

export const ColorConfiguratorPicker = ({ onChange, value, defaultColor, children }: Props) => {
  const { token } = theme.useToken()
  const { isMobile } = useScreenWidth()
  const [customPresets, setCustomPresets] = useState<Presets[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const [initialColor, setInitialColor] = useState(defaultColor)

  const antdPresets = genPresets(
    {
      primary: generate(token.colorPrimary),
      red,
      green,
      cyan,
    },
    isMobile,
  )

  useEffect(() => {
    setCustomPresets([
      {
        label: 'Residen',
        colors: [
          ...getCSSVariables('primary'),
          ...getCSSVariables('secondary'),
          ...getCSSVariables('tertiary'),
          ...getCSSVariables('quaternary'),
        ],
        key: 'residen',
      },
      {
        label: 'System',
        colors: (() => {
          const styles = getComputedStyle(document.documentElement)
          return [
            styles.getPropertyValue('--success').trim(),
            styles.getPropertyValue('--attention').trim(),
            styles.getPropertyValue('--warning').trim(),
            styles.getPropertyValue('--danger').trim(),
            styles.getPropertyValue('--purple').trim(),
            styles.getPropertyValue('--blue').trim(),
            styles.getPropertyValue('--site').trim(),
            styles.getPropertyValue('--background').trim(),
            styles.getPropertyValue('--white').trim(),
            styles.getPropertyValue('--black').trim(),
          ]
        })(),
        key: 'system-custom',
        defaultOpen: !isMobile,
      },
    ])
  }, [isMobile])

  useEffect(() => {
    if (isOpen) setInitialColor(value)
  }, [isOpen])

  const customPanelRender: ColorPickerProps['panelRender'] = (
    _,
    { components: { Picker, Presets } },
  ) => (
    <Row justify='space-between' wrap gutter={[16, 16]} className={styles.colorPickerRow}>
      <Col flex='auto' className={styles.colorPickerColumn}>
        <Picker />
        <Row gutter={[8, 8]}>
          <Col>
            <Button
              proportion={BUTTON_PROPORTION.SMALL}
              modifier={BUTTON_MODIFIER.DEFAULT}
              onClick={() => {
                onChange(initialColor)
                setIsOpen(false)
              }}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button proportion={BUTTON_PROPORTION.SMALL} onClick={() => setIsOpen(false)}>
              Select
            </Button>
          </Col>
        </Row>
      </Col>
      <Divider type='vertical' style={{ height: 'auto' }} />
      <Col flex='auto' xs={24} sm={12} className={styles.presetsColumn}>
        <Presets />
      </Col>
    </Row>
  )

  return (
    <ColorPicker
      destroyTooltipOnHide
      open={isOpen}
      placement='bottom'
      defaultValue={value}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) onChange(initialColor)
      }}
      rootClassName={styles.colorPickerContainer}
      presets={[...customPresets, ...antdPresets]}
      panelRender={customPanelRender}
      onChange={(color) => onChange(color.toHexString())}
      styles={{
        popupOverlayInner: {
          width: 480,
          maxWidth: '100%',
        },
      }}>
      {children}
    </ColorPicker>
  )
}
